import { PROTOCOL_TEST_IDS } from '@/components/admin/protocoles/contracts';
import { problemCountLabel, problemSentence } from '@/lib/protocol-labels';
import type { ProtocolProblem } from '@matter_extractor/shared';
import { TriangleAlert } from 'lucide-react';
import { useState } from 'react';

/**
 * La pastille jaune « N problèmes » de l'en-tête, et ce qu'elle déplie (refonte
 * modulaire §7.1; maquette `editeur-protocole.html`, `renderHead`).
 *
 * §7.1 hangs it in the header beside the save button: « plus la pastille jaune
 * "N problèmes" quand l'enregistrement d'une version refuserait, qui liste les
 * problèmes avec un lien vers chaque étape ». It rides `renderHeaderExtra`,
 * the slot the versioned layer grew for it in #49.
 *
 * ## It is a WARNING and never an error, and the sentence says so
 *
 * The problems do not block the draft: « L'enregistrement d'une version les
 * refusera. Le brouillon est conservé. » A protocol of nine steps is built over
 * days, and a screen that shouted in red at a step nobody has finished yet would
 * be shouting for most of that time. Jaune is the charte's tone for « read this
 * before you commit », which is exactly the moment this pill is about.
 *
 * ## Each line carries « ouvrir », and that is what makes the pill useful
 *
 * A problem NAMES its step (`ProtocolProblem` carries `stepId`), so the list is
 * a set of destinations rather than a report. Without the button an operator
 * reads « Étape 6 · Approfondissement n'a pas de plafond » and then hunts for
 * step 6 in a frise that scrolls.
 *
 * ## The panel is CLOSED at mount and opens on the click
 *
 * The mockup opens it under the pill, over the page. Component state and not a
 * search param, the régime « Plus de filtres » already holds: it is a posture of
 * the reader and not a property of what is on screen.
 */

export interface ProtocolProblemsPillProps {
  /** What `protocolProblems` answers on the draft's steps - empty renders nothing. */
  problems: readonly ProtocolProblem[];
  /** Select the step a line names, and close the panel. */
  onGoTo: (stepId: string) => void;
}

export function ProtocolProblemsPill({ problems, onGoTo }: ProtocolProblemsPillProps) {
  const [open, setOpen] = useState(false);

  /*
    NOTHING when there is nothing wrong, and not a green « aucun problème »: the
    pill is an interruption, and an interruption that fires on the healthy state
    is furniture. It is also why `open` is not reset here - the component simply
    goes, and comes back closed.
  */
  if (problems.length === 0) return null;

  return (
    <div className="relative">
      <button
        type="button"
        data-testid={PROTOCOL_TEST_IDS.problems}
        aria-expanded={open}
        onClick={() => setOpen((was) => !was)}
        className="inline-flex h-11 cursor-pointer items-center gap-2 rounded-pill border-2 border-transparent bg-jaune-tint px-4 font-semibold text-jaune-deep text-sm hover:border-jaune-deep focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
      >
        <TriangleAlert aria-hidden="true" className="size-4.5" />
        {problemCountLabel(problems.length)}
      </button>

      {open ? (
        <div
          data-testid={PROTOCOL_TEST_IDS.problemsList}
          className="absolute right-0 z-30 mt-2 w-110 max-w-[80vw] space-y-1 rounded-md border-2 border-gray-200 bg-white p-4 text-left shadow-lg"
        >
          <h2 className="font-semibold text-bleu-nuit text-sm">Problèmes</h2>
          <p className="text-gray-600 text-xs leading-4">
            L’enregistrement d’une version les refusera. Le brouillon est conservé.
          </p>
          {problems.map((problem) => (
            <div
              key={`${problem.kind}-${problemSentence(problem)}`}
              className="flex items-center gap-2 border-gray-100 border-t pt-2 text-gray-800 text-sm"
            >
              <TriangleAlert aria-hidden="true" className="size-4 shrink-0 text-jaune-deep" />
              <span className="min-w-0 flex-1">{problemSentence(problem)}</span>
              {/* The two problems of the Rangement section name no step: nothing to open. */}
              {'stepId' in problem ? (
                <button
                  type="button"
                  onClick={() => {
                    setOpen(false);
                    onGoTo(problem.stepId);
                  }}
                  className="shrink-0 cursor-pointer text-bleu-nuit text-sm underline underline-offset-2 hover:text-bleu-nuit-deep focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  ouvrir
                </button>
              ) : null}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}
